import { ITermTypeNameService } from './../../services/term_type_name_service';
import { ILog } from './../../../global/types';
import { IDelegateController, IDelegateResponder, IDelegateConstant } from 'ui-ng-delegate';
import { SitesetupConfiguration } from '../../../sitesetup/config/sitesetup_configuration';
import { ITransferTermTypesDatasource } from '../../datasources/transfer_term_types_datasource';
import * as _4 from 'lodash4';
import { TransferTermTypeQuery } from '../../queries/transfer_term_type_query';
import { ICommand } from 'core-ts-types';
import { Course } from './../../builders/course_builder';
import { College } from './../../builders/college_builder';
import { TransferConfiguration } from './../../config/transfer_configuration';
import { ErrorsDict, IFormErrorService } from './../../../global/services/form_error_service/form_error_service';
import { IServerResponse } from './../../../global/interfaces/server_response';
import { TransferGradeQuery } from '../../queries/transfer_grade_query';

export interface ICourseTileScope extends ng.IScope {
  course: Course;
  college: College;
  onExitTab: () => void;
  displayCourseAdditionalInfo: string;
}

interface IPendingCourseFormOutputDetail {
  value: Course;
}
interface IPendingCourseFormOutput {
  detail: IPendingCourseFormOutputDetail;
}

export class TransferCourseTileController {
  private errors: ErrorsDict;
  private showAsPending: boolean = false;
  private uuid = _4.uniqueId();
  private updateStudentCourseDebounced: ng.IPromise<void>;
  private DEBOUNCE_TIME = 500;
  private termType: String;
  private placeHolder: String;

  constructor(
    private deselectTransferCourseCommand: ICommand,
    private removeTransferPendingCourseCommand: ICommand,
    private ngSitesetupConfiguration: SitesetupConfiguration,
    private ngTransferTermTypeQuery: TransferTermTypeQuery,
    private updateTransferStudentCourseCommand: ICommand,
    private checkTransferStudentCourseIsReviewedCommand: ICommand,
    private updateReviewCourseStatusCommand: ICommand,
    private log: ILog,
    private $element: ng.IAugmentedJQuery,
    private addTransferMajorPendingCourseCommand: ICommand,
    private transferCourseTileDelegate: any,
    private ngTransferConfiguration: TransferConfiguration,
    private $scope: ICourseTileScope,
    private $timeout: ng.ITimeoutService,
    private formErrorService: IFormErrorService,
    private ngTermTypeNameService: ITermTypeNameService,
    private ngDisplayGradeService: ng.IServiceProvider,
    private ngTransferGradeQuery: TransferGradeQuery
  ) {
    $scope.onExitTab = () => {
      this.$scope.course.isPending
        ? this.removeTransferPendingCourseCommand($scope.course.college_nk, $scope.course)
        : this.deselectTransferCourseCommand($scope.course.college_nk, $scope.course);
    };

    $scope.displayCredits = (): number => {
      return Math.round(parseInt(this.$scope.course.credits, 10));
    };
    this.reflectSelectionChanges();
  }

  private reflectSelectionChanges() {
    this.termType = this.ngTermTypeNameService(this.$scope.course.term_type, '');
    this.placeHolder = this.ngTermTypeNameService(this.$scope.course.term_type, 'Term');
  }

  private appendUUID(attr: string) {
    return attr + this.uuid;
  }

  private shouldDisplayTerm(termType: string, termYear: string): boolean {
    return this.ngSitesetupConfiguration.isAdditionalCourseInfoFieldEnabled('term') && !!termType && !!termYear;
  }

  private shouldDisplayCourseAdditionalInfo() {
    return (
      this.$scope.displayCourseAdditionalInfo &&
      !!this.ngSitesetupConfiguration.config.core_config.enable_additional_course_info
    );
  }

  private checkForPendingCourse() {
    const hasValidYear = _.isNumber(this.$scope.course.term_year);
    const hasValidTermTypeChosen = !_.isEmpty(this.$scope.course.term_type);
    const readyToCheckPendingCourse = hasValidYear && hasValidTermTypeChosen;
    if (!readyToCheckPendingCourse) {
      return;
    }
    this.checkTransferStudentCourseIsReviewedCommand(this.$scope.course);
  }

  private pendingCourseFormOutputChanged($event: IPendingCourseFormOutput) {
    return this.updateStudentCourse($event.detail.value);
  }

  private formInputIsValid(id: string) {
    const formInput = this.$element[0].querySelector(id);
    if (!formInput) {
      return false;
    }
    return formInput.hasAttribute('invalid');
  }

  private validPendingCourseForm() {
    const fieldIds = ['#nc_term_type', '#nc_term_year', '#nc_credits', '#nc_grade'];
    const formFieldIds = fieldIds.map((id) => this.appendUUID(id));
    const invalidFormFields = formFieldIds.filter((id) => this.formInputIsValid(id));
    return _4.isEmpty(invalidFormFields);
  }

  private prepareForCourseUpdate($event: FocusEvent) {
    const addToPendingActive = this.showAsPending;
    if (addToPendingActive) {
      return;
    }

    const focusedElement = $event.relatedTarget as HTMLElement;
    if (!focusedElement) {
      return;
    }

    const blurTriggeredElement = $event.currentTarget as HTMLElement;
    if (!blurTriggeredElement) {
      return;
    }

    const blurHappenedInsideChild = blurTriggeredElement.contains(focusedElement);
    if (blurHappenedInsideChild) {
      return;
    }

    if (!this.validPendingCourseForm()) {
      return;
    }

    const clickedOnDontAddButton = focusedElement
      ? focusedElement.getAttribute('name') === 'dont-add-course-button'
      : false;

    if (clickedOnDontAddButton) {
      return;
    }
    return this.debouncedUpdateStudentCourse();
  }

  private debouncedUpdateStudentCourse() {
    const addToPendingActive = this.showAsPending;
    if (addToPendingActive) {
      return;
    }

    if (this.updateStudentCourseDebounced) {
      this.$timeout.cancel(this.updateStudentCourseDebounced);
    }
    this.updateStudentCourseDebounced = this.$timeout(
      () => this.updateStudentCourse(this.$scope.course),
      this.DEBOUNCE_TIME
    );
  }

  private updateStudentCourse(course: Course) {
    this.errors = {};
    this.reflectSelectionChanges();
    this.updateTransferStudentCourseCommand(course)
      .then(this.checkForPendingCourse.bind(this))
      .catch((response: ng.IHttpPromiseCallbackArg<IServerResponse>) => {
        this.errors = this.formErrorService(response.data!.error);
      });
  }

  private convertToPendingCourse() {
    this.updateReviewCourseStatusCommand(this.$scope.course, true).then(() => {
      this.showAsPending = true;
    });
  }

  private addPendingCourse() {
    const addPendingCourseModePresent = this.showAsPending;
    if (!addPendingCourseModePresent) {
      return;
    }
    this.errors = {};
    this.addTransferMajorPendingCourseCommand(this.$scope.college, this.$scope.course)
      .then((pendingCourse: Course) => {
        this.$scope.onExitTab();
        this.showAsPending = false;
        this.errors = {};
        this.log.user.success({
          content: `Your course ${this.$scope.course.display} has been added for review`,
          timeout: 10000,
          dismissOnTimeout: true,
          dismissButton: true
        });
        this.$timeout(() => {
          this.transferCourseTileDelegate.handle(pendingCourse.id).strobe();
        });
      })
      .catch((response: ng.IHttpPromiseCallbackArg<IServerResponse>) => {
        this.errors = this.formErrorService(response.data!.error);
      });
  }

  private cancelPendingCourse() {
    this.showAsPending = false;
    this.$scope.course.isReviewed = true;
    if (this.updateStudentCourseDebounced) {
      this.$timeout.cancel(this.updateStudentCourseDebounced);
    }
  }

  private showAdditionalCourseForm() {
    return !this.$scope.course.isPending && !this.$scope.course.isRejected && this.shouldDisplayCourseAdditionalInfo();
  }
}

class TransferCourseTileDelegate {
  constructor(DelegateController: IDelegateController, transferCourseTileDelegateConfiguration: IDelegateConstant) {
    DelegateController(this, transferCourseTileDelegateConfiguration);
  }
}

angular
  .module('capability-ng-transfer')
  .service('transferCourseTileDelegate', TransferCourseTileDelegate)
  .constant('transferCourseTileDelegateConfiguration', {
    uniqueComponent: 'transfer-course-tile',
    vocabulary: {
      strobe: function(scope: ng.IScope, el: JQuery) {
        el.children('.tile').addClass('transfer-course-tile--strobe');
      }
    }
  })
  .controller('ngTransferCourseTileController', TransferCourseTileController)
  .directive('transferCourseTile', function(
    DelegateResponder: IDelegateResponder,
    transferCourseTileDelegateConfiguration: IDelegateConstant
  ): ng.IDirective {
    return {
      controller: 'ngTransferCourseTileController',
      templateUrl: 'lib/transfer/components/course_tile/course_tile.html',
      controllerAs: '$ctrl',
      scope: {
        course: '<',
        college: '<',
        displayCourseAdditionalInfo: '<'
      },
      link: DelegateResponder(transferCourseTileDelegateConfiguration, function(scope, elements, attrs) {})
    };
  });



// WEBPACK FOOTER //
// ./app/lib/transfer/components/course_tile/course_tile.ts